import { useQuery } from '@tanstack/react-query';
import { Autocomplete } from 'src/components/Autocomplete';
import { TextField } from 'src/components/TextField';
import { Location } from 'src/models/location/_types';
import { fetchLocations } from 'src/models/location/location';

interface LocationFilterProps {
  label: string;
  value: string | undefined;
  setValue: (newVal: string) => void;
  valueOnly?: boolean;
  multiple?: boolean;
  includeNoLocation?: boolean;
}

// Special "No Location" option
const NO_LOCATION_OPTION = { id: -1, name: 'No Location' };

export const LocationFilter: React.FC<LocationFilterProps> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple,
  includeNoLocation = false,
}) => {
  const { data: locations = { locations: [] } } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations({ fetchAll: true }),
  });

  const locationIds = value ? String(value).split(',') : [];
  const hasNoLocation =
    locationIds.includes('-1') ||
    locationIds.includes(String(NO_LOCATION_OPTION.id));

  // Combine locations with "No Location" option if enabled
  const allOptions = [
    ...(includeNoLocation ? [NO_LOCATION_OPTION] : []),
    ...locations.locations,
  ];

  let selectedLocation = undefined as
    | (Location | typeof NO_LOCATION_OPTION)
    | undefined;
  let selectedLocations = [] as (Location | typeof NO_LOCATION_OPTION)[];

  if (multiple) {
    selectedLocations = allOptions.filter((option) => {
      if (option.id === NO_LOCATION_OPTION.id) {
        return hasNoLocation;
      }
      return locationIds?.includes(String(option.id));
    });
  } else {
    if (hasNoLocation) {
      selectedLocation = NO_LOCATION_OPTION;
    } else {
      selectedLocation = locations.locations.find(
        (l) => String(l.id) === String(value)
      );
    }
  }

  if (valueOnly) {
    if (multiple) {
      const names = selectedLocations.map((l) => l.name);
      return <>{names.join(', ')}</>;
    }
    return <>{selectedLocation?.name || ''}</>;
  }

  return (
    <Autocomplete
      multiple={multiple}
      options={allOptions}
      getOptionLabel={(option) => option.name}
      value={multiple ? selectedLocations : selectedLocation || null}
      onChange={(_, newValue) => {
        if (multiple) {
          const values = Array.isArray(newValue) ? newValue : [];
          setValue(values.map((l) => String(l.id)).join(','));
        } else {
          setValue(newValue ? String(newValue.id) : '');
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Select Location" />
      )}
    />
  );
};
